import axios from 'axios';
import type { AxiosInstance, AxiosRequestConfig } from 'axios';
import { API_ACTIVITY_EVENT, API_BASE_URL } from '../constants/api';

// Extend AxiosRequestConfig to include our custom option
declare module 'axios' {
  export interface AxiosRequestConfig {
    skipTokenRefresh?: boolean;
  }
}

class ApiService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token and signal activity for inactivity timer
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent(API_ACTIVITY_EVENT));
          }
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle token refresh
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        const status = error.response?.status;
        const isOnLoginPage = window.location.pathname === '/login';
        const isLoginEndpoint = originalRequest?.url?.includes('/auth/login');
        const isRefreshEndpoint = originalRequest?.url?.includes('/auth/refresh');
        const isLogoutEndpoint = originalRequest?.url?.includes('/auth/logout');
        const isRevokeEndpoint = originalRequest?.url?.includes('/auth/revoke');
        const skipRefresh = originalRequest?.skipTokenRefresh === true;

        // If error is 401 and we haven't already tried to refresh
        if (status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          // Don't try to refresh for login/refresh/logout/revoke endpoints, if we're already on login page, or if explicitly skipped
          if (isLoginEndpoint || isRefreshEndpoint || isLogoutEndpoint || isRevokeEndpoint || isOnLoginPage || skipRefresh) {
            return Promise.reject(error);
          }

          const refreshToken = localStorage.getItem('refreshToken');
          if (refreshToken) {
            try {
              const response = await this.client.post<{ token: string; refreshToken: string; expiresAt: string }>('/auth/refresh', { refreshToken });
              const { token, refreshToken: newRefreshToken } = response.data;

              // Update stored tokens
              localStorage.setItem('authToken', token);
              if (newRefreshToken) {
                localStorage.setItem('refreshToken', newRefreshToken);
              }

              // Notify session refresh service to reschedule timers (avoids stale "session expiring" when interceptor refreshed)
              if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('tokenRefreshed'));
              }

              // Retry original request with new token
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return this.client(originalRequest);
            } catch (refreshError) {
              // Refresh failed, redirect to login
              localStorage.removeItem('authToken');
              localStorage.removeItem('refreshToken');
              window.location.href = '/login';
              return Promise.reject(refreshError);
            }
          } else {
            // No refresh token, redirect to login (but not if already on login page)
            localStorage.removeItem('authToken');
            if (!isOnLoginPage) {
              window.location.href = '/login';
            }
          }
        }

        return Promise.reject(error);
      }
    );
  }

  get<T>(url: string, config?: AxiosRequestConfig) {
    return this.client.get<T>(url, config);
  }

  post<T, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig) {
    return this.client.post<T>(url, data, config);
  }

  put<T, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig) {
    return this.client.put<T>(url, data, config);
  }

  delete<T>(url: string, config?: AxiosRequestConfig) {
    return this.client.delete<T>(url, config);
  }

  patch<T, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig) {
    return this.client.patch<T>(url, data, config);
  }

  async fetchWithAuth(input: string, init?: RequestInit, options?: { skipTokenRefresh?: boolean }): Promise<Response> {
    const isOnLoginPage = typeof window !== 'undefined' && window.location.pathname === '/login';
    const isLoginEndpoint = input.includes('/auth/login');
    const isRefreshEndpoint = input.includes('/auth/refresh');
    const isLogoutEndpoint = input.includes('/auth/logout');
    const isRevokeEndpoint = input.includes('/auth/revoke');
    const skipRefresh = options?.skipTokenRefresh === true;

    const headers = new Headers(init?.headers ?? undefined);
    const authToken = localStorage.getItem('authToken');
    if (authToken && !headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${authToken}`);
    }
    if (authToken && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(API_ACTIVITY_EVENT));
    }

    const response = await fetch(`${API_BASE_URL}${input}`, {
      ...init,
      headers,
    });

    if (response.status !== 401 || isLoginEndpoint || isRefreshEndpoint || isLogoutEndpoint || isRevokeEndpoint || isOnLoginPage || skipRefresh) {
      return response;
    }

    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      localStorage.removeItem('authToken');
      if (!isOnLoginPage) {
        window.location.href = '/login';
      }
      return response;
    }

    try {
      const refreshResponse = await this.client.post<{ token: string; refreshToken: string; expiresAt: string }>(
        '/auth/refresh',
        { refreshToken }
      );
      const { token, refreshToken: newRefreshToken } = refreshResponse.data;

      localStorage.setItem('authToken', token);
      if (newRefreshToken) {
        localStorage.setItem('refreshToken', newRefreshToken);
      }
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('tokenRefreshed'));
      }

      const retryHeaders = new Headers(init?.headers ?? undefined);
      retryHeaders.set('Authorization', `Bearer ${token}`);
      return fetch(`${API_BASE_URL}${input}`, {
        ...init,
        headers: retryHeaders,
      });
    } catch {
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      if (!isOnLoginPage) {
        window.location.href = '/login';
      }
      return response;
    }
  }
}

export const apiService = new ApiService();


